import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  TrendingUp, MapPin, Loader2, ExternalLink, Briefcase,
  ArrowUpRight, ArrowDownRight, Calculator, Banknote, Home,
  BarChart3, CheckCircle2, XCircle, Shield
} from 'lucide-react';

const SharedAnalysisPage = () => {
  const { shareToken } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchShared();
  }, [shareToken]);

  const fetchShared = async () => {
    try {
      const resp = await axios.get(`${API}/portfolio/shared/${shareToken}`);
      setAnalysis(resp.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Analyse non trouvée ou lien expiré' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#0D47A1] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">{error}</h1>
          <Link to="/"><Button variant="outline">Retour à l'accueil</Button></Link>
        </div>
      </div>
    );
  }

  const bien = analysis.bien || {};
  const rent = analysis.rentabilite || {};
  const credit = analysis.credit_25ans || {};
  const isPositif = analysis.verdict === 'positif';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#00B050] to-[#0D47A1] flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">10 Millions Investor</h1>
              <p className="text-xs text-slate-500">Analyse d'investissement partagée</p>
            </div>
          </div>
          {analysis.shared_by && (
            <Badge variant="outline" className="text-xs" data-testid="shared-by-badge">
              Partagé par {analysis.shared_by}
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Verdict banner */}
        <div className={`rounded-2xl p-6 mb-6 ${isPositif ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-orange-500 to-red-500'} text-white`}
          data-testid="verdict-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isPositif ? <ArrowUpRight className="h-10 w-10" /> : <ArrowDownRight className="h-10 w-10" />}
              <div>
                <h2 className="text-2xl font-bold">{isPositif ? 'Cash-flow positif' : 'Cash-flow négatif'}</h2>
                <p className="text-white/80">{bien.titre}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{bien.prix?.toLocaleString()}€</div>
              {bien.surface > 0 && <div className="text-white/80">{bien.surface}m² {bien.localisation && `· ${bien.localisation}`}</div>}
            </div>
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="metric-rendement-brut">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="h-5 w-5 text-[#0D47A1]" />
              </div>
              <div className="text-2xl font-bold text-[#0D47A1]">{rent.rendement_brut?.toFixed(1)}%</div>
              <div className="text-xs text-slate-500 uppercase mt-1">Rendement brut</div>
            </CardContent>
          </Card>
          <Card data-testid="metric-rendement-net">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                <BarChart3 className="h-5 w-5 text-[#00B050]" />
              </div>
              <div className="text-2xl font-bold text-[#00B050]">{rent.rendement_net?.toFixed(1)}%</div>
              <div className="text-xs text-slate-500 uppercase mt-1">Rendement net</div>
            </CardContent>
          </Card>
          <Card data-testid="metric-cashflow">
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${rent.cash_flow_mensuel >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                {rent.cash_flow_mensuel >= 0 ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
              </div>
              <div className={`text-2xl font-bold ${rent.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {rent.cash_flow_mensuel >= 0 ? '+' : ''}{rent.cash_flow_mensuel?.toLocaleString()}€
              </div>
              <div className="text-xs text-slate-500 uppercase mt-1">Cash-flow / mois</div>
            </CardContent>
          </Card>
          <Card data-testid="metric-mensualite">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                <Banknote className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="text-2xl font-bold text-indigo-600">{credit.mensualite?.toLocaleString()}€</div>
              <div className="text-xs text-slate-500 uppercase mt-1">Mensualité crédit</div>
            </CardContent>
          </Card>
        </div>

        {/* Detail cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Property details */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Home className="h-5 w-5 text-[#0D47A1]" />
                <h3 className="font-semibold text-slate-800">Le bien</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Prix d'achat</span><span className="font-semibold">{bien.prix?.toLocaleString()}€</span></div>
                {bien.surface > 0 && <div className="flex justify-between"><span className="text-slate-500">Surface</span><span className="font-semibold">{bien.surface}m²</span></div>}
                {bien.nb_pieces > 0 && <div className="flex justify-between"><span className="text-slate-500">Pièces</span><span className="font-semibold">{bien.nb_pieces}</span></div>}
                {bien.localisation && <div className="flex justify-between"><span className="text-slate-500">Localisation</span><span className="font-semibold flex items-center gap-1"><MapPin className="h-3 w-3" />{bien.localisation}</span></div>}
                {bien.dpe && <div className="flex justify-between"><span className="text-slate-500">DPE</span><Badge variant="outline">{bien.dpe}</Badge></div>}
                <div className="flex justify-between"><span className="text-slate-500">Loyer estimé</span><span className="font-semibold text-[#00B050]">{analysis.loyer_estime?.toLocaleString()}€/mois</span></div>
                {bien.url && (
                  <a href={bien.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#0D47A1] hover:underline mt-2">
                    <ExternalLink className="h-4 w-4" /> Voir l'annonce
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial details */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="h-5 w-5 text-[#00B050]" />
                <h3 className="font-semibold text-slate-800">Analyse financière</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Rendement brut</span><span className="font-semibold text-[#0D47A1]">{rent.rendement_brut?.toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Rendement net</span><span className="font-semibold text-[#00B050]">{rent.rendement_net?.toFixed(1)}%</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cash-flow mensuel</span>
                  <span className={`font-semibold ${rent.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {rent.cash_flow_mensuel >= 0 ? '+' : ''}{rent.cash_flow_mensuel?.toLocaleString()}€
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Cash-flow annuel</span><span className={`font-semibold ${rent.cash_flow_annuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>{rent.cash_flow_annuel >= 0 ? '+' : ''}{rent.cash_flow_annuel?.toLocaleString()}€</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Impôts annuels</span><span className="font-semibold text-red-600">-{rent.impots_annuels?.toLocaleString()}€</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Régime recommandé</span><Badge className="bg-[#0D47A1] text-white text-xs">{rent.regime_label}</Badge></div>
                <hr className="border-slate-100" />
                <div className="flex justify-between"><span className="text-slate-500">Mensualité crédit (25 ans)</span><span className="font-semibold">{credit.mensualite?.toLocaleString()}€</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Coût total du crédit</span><span className="font-semibold">{credit.cout_total?.toLocaleString()}€</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Coût des intérêts</span><span className="font-semibold text-red-600">{credit.cout_interets?.toLocaleString()}€</span></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="cta-section">
          <Shield className="h-8 w-8 text-[#0D47A1] mx-auto mb-3" />
          <h3 className="font-bold text-slate-900 mb-1">Analyse propulsée par 10 Millions Investor</h3>
          <p className="text-sm text-slate-500 mb-4">
            Rejoins le Club pour accéder à l'Agent IA, au portfolio d'investissements et aux alertes automatiques
          </p>
          <Link to="/">
            <Button className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white" data-testid="join-club-btn">
              Découvrir 10 Millions Investor
            </Button>
          </Link>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Les données sont fournies à titre indicatif. Loyer et charges estimés. Consultez un professionnel avant tout investissement.
        </p>
      </main>
    </div>
  );
};

export default SharedAnalysisPage;
